import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../../Store';
import { OneLPSsdkParamsContext } from '../../../onelps-sdk-params-context';
import { cookieExist, getCookieValue } from '../../../helpers/browser-cookies';
import { COOKIE_NOTIFICATION_NAME, COOKIE_NOTIFICATION_VALUE_ACCEPT } from '../../../helpers/cookie-overlay';
import { loginStatus } from '../../../helpers/remember-me/login-status';
import { DEFAULT_ERROR_CODE } from '../../../helpers/server-messages';
import { STEP2_SERVER_ERROR_RELOAD_AFTER } from '../../password-forgotten/Step2';
import { useTranslation } from 'react-i18next';
import { readAllMessages } from '../../../helpers/server-messages';
import { getPrivacyUrl, getTermsAndConditionsUrl, getImprintUrl } from '../../../helpers/legal-urls';
import { withTranslations } from './loadTranslations';
import { getBrowserLocale } from '../../../helpers/browser-navigator';
import { isForgotPwTokenErrorCode } from '../../../helpers/password-forgotten/common';
import { isResetPwLink } from '../../../helpers/password-forgotten/reset';
import { metrics } from '../../../metrics-config';
import { ENVIRONMENTS } from '../../../constants/environments';
import { getLoginHubBridge } from '../../../helpers/login-hub-bridge';

export const withCommonPageFunctionality = function(Component) {
	const EnhancedWrapper = function({ ...props }) {

		const [state, dispatch] = useContext(AppContext);
		const onelpsParams = useContext(OneLPSsdkParamsContext);

		const [isAuthenticated, setIsAuthenticated] = useState(null);
		const [username, setUsername] = useState('');
		const [forgotPwToken, setForgotPwToken] = useState(null);
		const [hasForgotPwError, setHasForgotPwError] = useState(false);

		const { t } = useTranslation('translation');

		const optionalSnippets = onelpsParams.snippets.optional;

		useEffect(() => {
			if (state.language) {
				const language = state.language;
				const privacyUrl = getPrivacyUrl(language);
				const termsAndConditionsUrl = getTermsAndConditionsUrl(onelpsParams.gameId, language);
				const imprintUrl = getImprintUrl(onelpsParams.gameId, language);

				dispatch({ type: 'SET_PRIVACY_URL', payload: privacyUrl });
				dispatch({ type: 'SET_TERMS_AND_CONDITIONS_URL', payload: termsAndConditionsUrl });
				dispatch({ type: 'SET_IMPRINT_URL', payload: imprintUrl });
			}
		}, [state.language, dispatch, onelpsParams.gameId]);

		useEffect(() => {
			if (
				(optionalSnippets && optionalSnippets.length === 0) ||
				(optionalSnippets && optionalSnippets.length > 0 && !optionalSnippets[0])
			) {
				dispatch({ type: 'MARK_TRACKING_EXECUTED' });
			}
		}, [dispatch, optionalSnippets]);

		useEffect(() => {
			if(cookieExist(COOKIE_NOTIFICATION_NAME) &&
			getCookieValue(COOKIE_NOTIFICATION_NAME) === COOKIE_NOTIFICATION_VALUE_ACCEPT) {
				dispatch({ type: 'ACCEPT_THIRD_PARTY_TRACKING' });
			}
		}, [dispatch]);

		useEffect(() => {
			async function fetchLoginStatus() {
				try {
					const stateLoginStatus = await loginStatus(dispatch);

					metrics.bindPageReadyEvent( { playerId: stateLoginStatus.playerId } );


					setIsAuthenticated(stateLoginStatus.isAuthenticated);
	
					if (stateLoginStatus.isAuthenticated && stateLoginStatus.username) {
						dispatch({ type: 'SHOW_REMEMBERME_MODAL' });
						setUsername(stateLoginStatus.username);
					} else {
						dispatch({ type: 'ENABLE_LOGIN_INPUTS' });
					}
				} catch(e) {
					if (ENVIRONMENT !== ENVIRONMENTS.DEVELOPMENT) {
						dispatch({ type: 'SET_SERVER_ERROR_MESSAGE', payload: t(DEFAULT_ERROR_CODE) });
						dispatch({ type: 'SHOW_SERVER_ERROR' });
						metrics.bindPageReadyEvent({});
					}
				}
			}

			fetchLoginStatus();
		}, []);

		useEffect(() => {
			if (onelpsParams.forgotPw && onelpsParams.forgotPw.errorCode) {
				setHasForgotPwError(true);
				if (onelpsParams.oldReturningUserFlow && isForgotPwTokenErrorCode(onelpsParams.forgotPw.errorCode)) {
					dispatch({ type: 'SHOW_FORGOTPW_OVERLAY' });
				} else {
					dispatch({ type: 'SET_SERVER_ERROR_MESSAGE', payload: t(onelpsParams.forgotPw.errorCode) });
					dispatch({ type: 'SHOW_SERVER_ERROR' });

					setTimeout(function(){
						window.location.href = window.location.origin;
					}, STEP2_SERVER_ERROR_RELOAD_AFTER);
				}
			}
		}, [t, dispatch, onelpsParams.forgotPw]); 

		useEffect(() => {
			if(onelpsParams.forgotPw && onelpsParams.forgotPw.token) {
				setForgotPwToken(onelpsParams.forgotPw.token);
			}

			const serverMessages = readAllMessages();
			
			if (serverMessages.length > 0) {
				dispatch({ type: 'SET_SERVER_MESSAGES', payload: serverMessages });
			}
		}, [dispatch, onelpsParams.forgotPw]);

		useEffect(() => {
			const bridgeLocale = getLoginHubBridge().getLocale();
			if (onelpsParams.isMonolingual && onelpsParams.locale) {
				console.log('Locale from OneLPSParams: ' + onelpsParams.locale);
				dispatch({ type: 'SET_LOCALE', payload: onelpsParams.locale });
			} else if (bridgeLocale !== null) {
				console.log('Locale from client bridge: ' + bridgeLocale);
				dispatch({ type: 'SET_LOCALE', payload: bridgeLocale });
			} else {
				console.log('Locale from browser: ' + getBrowserLocale());
				dispatch({ type: 'SET_LOCALE', payload: getBrowserLocale() });
			}
		}, [dispatch, onelpsParams.isMonolingual, onelpsParams.locale]);

		useEffect(() => {
			if (isResetPwLink()) {
				dispatch({ type: 'SHOW_FORGOTPW_OVERLAY' });
			}
		}, [dispatch]);

		return (
			<Component
				{...props}
				isAuthenticated={isAuthenticated}
				username={username}
				forgotPwToken={forgotPwToken}
				hasForgotPwError={hasForgotPwError}
			/>
		);
	};
	EnhancedWrapper.displayName = 'wrapperWithCommonPageFunctionality';

	const EnhancedWrapperWithTranslations = withTranslations(EnhancedWrapper);

	return EnhancedWrapperWithTranslations;
};
